import { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Tabs,
  Descriptions,
  Avatar,
  Input,
  DatePicker,
  Select,
} from 'antd';
import {
  ShareAltOutlined,
  UserAddOutlined,
  GiftOutlined,
  DollarOutlined,
  SettingOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  UserOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCustom, useApiUrl } from '@refinedev/core';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

interface Referral {
  id: number;
  referrer: {
    id: number;
    fullName: string;
    phone: string;
    photo?: string;
    type: 'PASSENGER' | 'DRIVER';
  };
  referred: {
    id: number;
    fullName: string;
    phone: string;
    photo?: string;
    type: 'PASSENGER' | 'DRIVER';
  };
  status: 'PENDING' | 'COMPLETED' | 'BONUS_PAID';
  referrerBonus: number;
  referredBonus: number;
  referrerBonusPaid: boolean;
  referredBonusPaid: boolean;
  createdAt: string;
  completedAt?: string;
}

interface ReferralStats {
  totalReferrals: number;
  completedReferrals: number;
  pendingReferrals: number;
  totalBonusPaid: number;
  thisMonthReferrals: number;
  conversionRate: number;
  topReferrers: {
    id: number;
    fullName: string;
    phone: string;
    photo?: string;
    referralCount: number;
    totalBonus: number;
  }[];
}

interface ReferralConfig {
  referrerBonus: number;
  referredBonus: number;
  driverReferrerBonus: number;
  driverReferredBonus: number;
  minTripsToComplete: number;
  isActive: boolean;
}

export const ReferralList = () => {
  const apiUrl = useApiUrl();
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('list');

  // Fetch referrals
  const { data: referralsData, isLoading, refetch } = useCustom<{
    data: Referral[];
    total: number;
  }>({
    url: `${apiUrl}/admin/referrals`,
    method: 'get',
    config: {
      query: {
        search: searchText || undefined,
        status: statusFilter || undefined,
      },
    },
  });

  // Fetch stats
  const { data: statsData } = useCustom<ReferralStats>({
    url: `${apiUrl}/admin/referrals/stats`,
    method: 'get',
  });

  // Fetch config
  const { data: configData, refetch: refetchConfig } = useCustom<ReferralConfig>({
    url: `${apiUrl}/admin/referrals/config`,
    method: 'get',
  });

  const stats = statsData?.data;
  const referrals = referralsData?.data?.data || [];
  const config = configData?.data;

  const handleSaveConfig = async (values: any) => {
    setSaving(true);
    try {
      await apiClient.put('/admin/referrals/config', values);
      message.success(t('settings.saved'));
      setConfigModalOpen(false);
      refetchConfig();
    } catch (error) {
      message.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handlePayBonus = async (referralId: number, type: 'referrer' | 'referred') => {
    try {
      await apiClient.post(`/admin/referrals/${referralId}/pay-bonus`, { type });
      message.success(t('referrals.bonusPaid'));
      refetch();
    } catch (error) {
      message.error('Bonus berishda xatolik');
    }
  };

  const openConfigModal = () => {
    if (config) {
      configForm.setFieldsValue(config);
    }
    setConfigModalOpen(true);
  };

  const columns: ColumnsType<Referral> = [
    {
      title: t('referrals.referrer'),
      key: 'referrer',
      render: (_, record) => (
        <Space>
          <Avatar
            src={record.referrer.photo}
            icon={!record.referrer.photo && <UserOutlined />}
          />
          <div>
            <Text strong>{record.referrer.fullName}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.referrer.phone}
            </Text>
            <br />
            <Tag size="small" color={record.referrer.type === 'DRIVER' ? 'blue' : 'green'}>
              {record.referrer.type === 'DRIVER' ? 'Haydovchi' : "Yo'lovchi"}
            </Tag>
          </div>
        </Space>
      ),
    },
    {
      title: '',
      key: 'arrow',
      width: 50,
      render: () => <ArrowRightOutlined style={{ color: '#bbb' }} />,
    },
    {
      title: t('referrals.referred'),
      key: 'referred',
      render: (_, record) => (
        <Space>
          <Avatar
            src={record.referred.photo}
            icon={!record.referred.photo && <UserOutlined />}
          />
          <div>
            <Text strong>{record.referred.fullName}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.referred.phone}
            </Text>
            <br />
            <Tag size="small" color={record.referred.type === 'DRIVER' ? 'blue' : 'green'}>
              {record.referred.type === 'DRIVER' ? 'Haydovchi' : "Yo'lovchi"}
            </Tag>
          </div>
        </Space>
      ),
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const config: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
          PENDING: { color: 'orange', icon: <ClockCircleOutlined />, label: 'Kutilmoqda' },
          COMPLETED: { color: 'blue', icon: <CheckCircleOutlined />, label: 'Bajarildi' },
          BONUS_PAID: { color: 'green', icon: <GiftOutlined />, label: 'Bonus berildi' },
        };
        const c = config[status] || { color: 'default', icon: null, label: status };
        return (
          <Tag color={c.color} icon={c.icon}>
            {c.label}
          </Tag>
        );
      },
    },
    {
      title: t('common.amount'),
      key: 'bonuses',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>
            Taklif qiluvchi: {record.referrerBonus.toLocaleString()} so'm
            {record.referrerBonusPaid && (
              <Tag color="success" size="small" style={{ marginLeft: 4 }}>
                Berildi
              </Tag>
            )}
          </Text>
          <Text>
            Taklif qilingan: {record.referredBonus.toLocaleString()} so'm
            {record.referredBonusPaid && (
              <Tag color="success" size="small" style={{ marginLeft: 4 }}>
                Berildi
              </Tag>
            )}
          </Text>
        </Space>
      ),
    },
    {
      title: t('common.date'),
      key: 'date',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>Yaratildi: {dayjs(record.createdAt).format('DD.MM.YYYY')}</Text>
          {record.completedAt && (
            <Text type="secondary">
              Bajarildi: {dayjs(record.completedAt).format('DD.MM.YYYY')}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          {record.status === 'COMPLETED' && !record.referrerBonusPaid && (
            <Button
              type="primary"
              size="small"
              icon={<GiftOutlined />}
              onClick={() => handlePayBonus(record.id, 'referrer')}
            >
              Taklif qiluvchiga
            </Button>
          )}
          {record.status === 'COMPLETED' && !record.referredBonusPaid && (
            <Button
              size="small"
              icon={<GiftOutlined />}
              onClick={() => handlePayBonus(record.id, 'referred')}
            >
              Taklif qilinganga
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card>
            <Statistic
              title={t('referrals.totalReferrals')}
              value={stats?.totalReferrals || 0}
              prefix={<ShareAltOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title={t('referrals.completed')}
              value={stats?.completedReferrals || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#27AE60' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title={t('referrals.pending')}
              value={stats?.pendingReferrals || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#F39C12' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title={t('referrals.thisMonth')}
              value={stats?.thisMonthReferrals || 0}
              prefix={<UserAddOutlined />}
              valueStyle={{ color: '#3498DB' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title={t('referrals.conversionRate')}
              value={stats?.conversionRate || 0}
              suffix="%"
              precision={1}
              valueStyle={{ color: '#9B59B6' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title={t('referrals.totalBonusPaid')}
              value={stats?.totalBonusPaid || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#E74C3C' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card
        title={
          <Space>
            <ShareAltOutlined />
            <span>Referal dasturi</span>
          </Space>
        }
        extra={
          <Button icon={<SettingOutlined />} onClick={openConfigModal}>
            Sozlamalar
          </Button>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'list',
              label: (
                <span>
                  <TeamOutlined />
                  Takliflar ro'yxati
                </span>
              ),
              children: (
                <div>
                  {/* Filters */}
                  <Space style={{ marginBottom: 16 }}>
                    <Input
                      placeholder="Qidirish..."
                      prefix={<SearchOutlined />}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      style={{ width: 200 }}
                      allowClear
                    />
                    <Select
                      placeholder="Holat"
                      value={statusFilter}
                      onChange={setStatusFilter}
                      style={{ width: 150 }}
                      allowClear
                      options={[
                        { value: 'PENDING', label: 'Kutilmoqda' },
                        { value: 'COMPLETED', label: 'Bajarildi' },
                        { value: 'BONUS_PAID', label: 'Bonus berildi' },
                      ]}
                    />
                  </Space>

                  <Table
                    columns={columns}
                    dataSource={referrals}
                    rowKey="id"
                    loading={isLoading}
                    pagination={{ pageSize: 10 }}
                  />
                </div>
              ),
            },
            {
              key: 'top',
              label: (
                <span>
                  <GiftOutlined />
                  Top taklif qiluvchilar
                </span>
              ),
              children: (
                <Table
                  dataSource={stats?.topReferrers || []}
                  rowKey="id"
                  columns={[
                    {
                      title: '#',
                      key: 'index',
                      width: 50,
                      render: (_, __, index) => index + 1,
                    },
                    {
                      title: 'Foydalanuvchi',
                      key: 'user',
                      render: (_, record) => (
                        <Space>
                          <Avatar
                            src={record.photo}
                            icon={!record.photo && <UserOutlined />}
                          />
                          <div>
                            <Text strong>{record.fullName}</Text>
                            <br />
                            <Text type="secondary">{record.phone}</Text>
                          </div>
                        </Space>
                      ),
                    },
                    {
                      title: 'Takliflar soni',
                      dataIndex: 'referralCount',
                      key: 'referralCount',
                      render: (count) => (
                        <Tag color="blue" icon={<UserAddOutlined />}>
                          {count} ta
                        </Tag>
                      ),
                    },
                    {
                      title: 'Jami bonus',
                      dataIndex: 'totalBonus',
                      key: 'totalBonus',
                      render: (bonus) => (
                        <Text type="success" strong>
                          {bonus.toLocaleString()} so'm
                        </Text>
                      ),
                    },
                  ]}
                  pagination={false}
                />
              ),
            },
            {
              key: 'config',
              label: (
                <span>
                  <SettingOutlined />
                  Joriy sozlamalar
                </span>
              ),
              children: config ? (
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Dastur holati">
                    <Tag color={config.isActive ? 'success' : 'default'}>
                      {config.isActive ? 'Faol' : 'Nofaol'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Bajarish uchun minimum sayohatlar">
                    {config.minTripsToComplete} ta
                  </Descriptions.Item>
                  <Descriptions.Item label="Yo'lovchi - Taklif qiluvchi bonusi">
                    {config.referrerBonus.toLocaleString()} so'm
                  </Descriptions.Item>
                  <Descriptions.Item label="Yo'lovchi - Taklif qilingan bonusi">
                    {config.referredBonus.toLocaleString()} so'm
                  </Descriptions.Item>
                  <Descriptions.Item label="Haydovchi - Taklif qiluvchi bonusi">
                    {config.driverReferrerBonus.toLocaleString()} so'm
                  </Descriptions.Item>
                  <Descriptions.Item label="Haydovchi - Taklif qilingan bonusi">
                    {config.driverReferredBonus.toLocaleString()} so'm
                  </Descriptions.Item>
                </Descriptions>
              ) : null,
            },
          ]}
        />
      </Card>

      {/* Config Modal */}
      <Modal
        title={t('referrals.title')}
        open={configModalOpen}
        onCancel={() => setConfigModalOpen(false)}
        footer={null}
        width={600}
      >
        <Form
          form={configForm}
          layout="vertical"
          onFinish={handleSaveConfig}
        >
          <Form.Item
            name="isActive"
            label="Dastur holati"
          >
            <Select
              options={[
                { value: true, label: 'Faol' },
                { value: false, label: 'Nofaol' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="minTripsToComplete"
            label="Bajarish uchun minimum sayohatlar soni"
            rules={[{ required: true, message: 'Qiymat kiriting' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} addonAfter="ta sayohat" />
          </Form.Item>

          <Title level={5}>Yo'lovchilar uchun bonuslar</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="referrerBonus"
                label="Taklif qiluvchi bonusi"
                rules={[{ required: true, message: 'Qiymat kiriting' }]}
              >
                <InputNumber
                  min={0}
                  step={1000}
                  style={{ width: '100%' }}
                  addonAfter="so'm"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/,/g, '') as any}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="referredBonus"
                label="Taklif qilingan bonusi"
                rules={[{ required: true, message: 'Qiymat kiriting' }]}
              >
                <InputNumber
                  min={0}
                  step={1000}
                  style={{ width: '100%' }}
                  addonAfter="so'm"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/,/g, '') as any}
                />
              </Form.Item>
            </Col>
          </Row>

          <Title level={5}>Haydovchilar uchun bonuslar</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="driverReferrerBonus"
                label="Taklif qiluvchi bonusi"
                rules={[{ required: true, message: 'Qiymat kiriting' }]}
              >
                <InputNumber
                  min={0}
                  step={1000}
                  style={{ width: '100%' }}
                  addonAfter="so'm"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/,/g, '') as any}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="driverReferredBonus"
                label="Taklif qilingan bonusi"
                rules={[{ required: true, message: 'Qiymat kiriting' }]}
              >
                <InputNumber
                  min={0}
                  step={1000}
                  style={{ width: '100%' }}
                  addonAfter="so'm"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/,/g, '') as any}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setConfigModalOpen(false)}>{t('common.cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                Saqlash
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
